import type { Region } from '~~/core/entities/Region';

import { PropertyFiltersPresenter } from '~/presenters/PropertyFiltersPresenter';
import { getUrlFromStaticDNS } from '@/utils';
import { PropertyTypeEnum } from '~/core/entities/CRMEnums';
import { getRegionMetadata } from './utils';

const regionName = 'Alto de Pinheiros';

const metaTags = getRegionMetadata(regionName, 'São Paulo', {
	gender: 'neutral'
});

const region: Region = {
	name: regionName,
	state: 'SP',
	city: 'São Paulo',
	path: '/bairros/alto-de-pinheiros',
	image: getUrlFromStaticDNS('/images/regions/alto-de-pinheiros/banner.webp'),
	metaTags,
	info: {
		title: '',
		description:
			'São Paulo é uma cidade vibrante e diversificada, conhecida por sua infraestrutura moderna, opções culturais ricas e oportunidades de negócios incomparáveis. Morar em São Paulo significa estar próximo de tudo: excelentes restaurantes, shoppings de alto padrão, parques, centros culturais e muito mais. Bairros como Jardins, Moema e Itaim Bibi oferecem uma qualidade de vida excepcional com suas ruas arborizadas e serviços de alto nível.</br></br>Optar pela Pilar para encontrar seu imóvel ideal em São Paulo garante acesso a uma seleção exclusiva de propriedades de alto padrão. Nossa equipe de corretores qualificados está pronta para oferecer suporte personalizado, desde a busca do imóvel perfeito até a finalização da compra. Com a Pilar, você encontra segurança, confiança e a certeza de estar fazendo o melhor investimento. Descubra o seu próximo lar em São Paulo com a Pilar.'
	},
	questions: [
		{
			question: `Onde fica ${regionName}?`,
			answer:
				'O Alto de Pinheiros está situado na cidade de São Paulo, na zona Oeste, no distrito de Pinheiros, sob a subprefeitura de mesmo nome. O bairro faz parte de uma região nobre da cidade e faz divisa com bairros como Vila Madalena e Pinheiros. O acesso ao bairro é facilitado por importantes avenidas, como a Avenida das Nações Unidas, a Avenida Professor Frederico Hermann Júnior, a Avenida Faria Lima e a Avenida Pedroso, que conectam o Alto de Pinheiros a diversas regiões da cidade.'
		},
		{
			question: `Como chegar ao ${regionName}?`,
			answer:
				'O Alto de Pinheiros é bem servido por transporte privado e público. Para quem viaja de carro, o bairro é acessado por diversas avenidas principais como a Avenida Faria Lima e a Avenida das Nações Unidas, que facilitam o tráfego até o bairro. Além disso, o Alto de Pinheiros possui boas ciclovias, permitindo fácil acesso ao Parque Villa-Lobos e aos bairros vizinhos. Para quem prefere o transporte público, o bairro conta com diversas linhas de ônibus que circulam pelas avenidas principais, como a Avenida Faria Lima, e está próximo das estações de metrô, oferecendo fácil acesso a várias regiões da cidade.'
		},
		{
			question: `O que fazer no ${regionName}?`,
			answer:
				'O Alto de Pinheiros oferece uma excelente combinação de tranquilidade e lazer, com destaque para o Parque Villa-Lobos, que é o coração verde do bairro, ideal para caminhadas, corridas e passeios de bicicleta. Ao lado, o Parque Cândido Portinari complementa essa área verde, abrigando a Roda Rico, a maior roda gigante da América Latina, que oferece uma vista panorâmica deslumbrante da região. Além disso, o Alto de Pinheiros conta com clubes exclusivos, como o Anhembi Tênis Clube e o Clube Alto dos Pinheiros, que oferecem uma vasta gama de atividades esportivas e sociais.'
		},
		{
			question: `Quais são os restaurantes no ${regionName}?`,
			answer:
				'O Alto de Pinheiros conta com uma excelente oferta gastronômica. O restaurante CÓR, localizado na Avenida São Gualter, oferece cortes de carne e pratos italianos contemporâneos, sendo uma ótima opção para a família. Para quem é fã de whisky, o Caledonia Whisky & Co. é um bar especializado em rótulos de todo o mundo. Já para os amantes de cerveja artesanal, o E.A.P – Empório Alto de Pinheiros tem mais de 200 rótulos e 40 torneiras diferentes. A Dona Deola também é um ótimo lugar para começar o dia, oferecendo um delicioso café da manhã.'
		}
	],
	hero: {
		title: regionName,
		description: 'Um refúgio no coração da zona oeste paulistana.',
		backgroundImage: getUrlFromStaticDNS(
			'/images/regions/alto-de-pinheiros/banner.webp'
		)
	},
	map: {
		title:
			'Alto de Pinheiros: restaurantes, parques e clubes para se apaixonar pelo bairro',
		content: `Localizado ao lado dos boêmios bairros de Pinheiros e Vila Madalena, o Alto de Pinheiros é um bairro para quem busca o equilíbrio perfeito entre a tranquilidade de um ambiente residencial e a energia urbana. Com ruas arborizadas e diversas praças, o bairro proporciona um estilo de vida que valoriza o bem-estar.<br><br>
        Com o parque Parque Villa-Lobos como quintal, o Alto de Pinheiros oferece opções de lazer diversas, além de ser conhecido por suas opções gastronômicas e um ótimo polo de lojas, com o Shopping Villa-Lobos, mercados e farmácias. Com ótimas escolas, como os tradicionais colégios Santa Cruz e Oswald de Andrade em seu entorno e clubes exclusivos, o Alto de Pinheiros é perfeito para famílias que buscam um refúgio no coração da zona oeste paulistana, mas não abrem mão de um agito aos fins de semana. A seguir, apresentamos seus encantos.`,
		image: '/images/regions/alto-de-pinheiros/map.jpeg',
		links: [
			{
				label: `Ver imóveis no ${regionName}`,
				url: PropertyFiltersPresenter.getPathFromFilters({
					propertyTypes: [],
					regions: [regionName],
					states: ['SP'],
					cities: ['São Paulo']
				})
			}
		]
	},
	content: [
		{
			info: {
				title: `Restaurantes no ${regionName}`,
				content:
					'Não faltam opções no Alto de Pinheiros e seus arredores para quem ama comer bem, de restaurantes tradicionais aos clássicos bares e padarias paulistanas. Falando nela, a Dona Deola conta com uma unidade completíssima no bairro, perfeita para quem quer tomar um bom café da manhã.<br><br>Localizado em um casarão na Av. São Gualter, o CÓR serve deliciosos cortes de carne, aliados a uma gastronomia italiana contemporânea. Sabemos que será uma ótima pedida para toda a família.<br><br>Mas, se seu negócio é mesmo um bom bar, vale dar um pulinho no bairro ao lado e explorar tudo que o Baixo Pinheiros tem a oferecer. Se você é amante de whisky, o Caledonia Whisky & Co, na rua Vupabussu, é um segredo guardado a sete chaves. Eleito o melhor bar de São Paulo pela Folha S. Paulo em 2023 e na lista dos 50 Best, na categoria “Discover”, o bar carrega rótulos do mundo todo para tomar puro ou em um bom drink . Agora, se você adora uma cerveja artesanal, o E.A.P - Empório Alto de Pinheiros, conta com 40 torneiras diferentes e mais de 200 rótulos em suas geladeiras.',
				class: 'row-span-2'
			},
			images: [
				{
					url: 'empty'
				},
				{ url: '/images/regions/alto-de-pinheiros/content/content-1.webp' },
				{
					url: '/images/regions/alto-de-pinheiros/content/content-2.webp',
					credits: 'Piotr Szulawski'
				}
			]
		},
		{
			info: {
				title: `Clubes no ${regionName}`,
				content:
					'Procurando um lugar para conhecer moradores e deixar as crianças se divertirem sem se preocupar? O Alto de Pinheiros conta com dois ótimos clubes para se associar.<br><br>Escondido no coração do bairro, na rua Orobó, o Anhembi Tênis Clube proporciona aos associados um ambiente perfeito para o lazer, esporte, convívio familiar, social e cultural. Com projeto arquitetônico assinado pelo renomado arquiteto João Batista Vilanova Artigas, o clube é pequeno, o que o torna super aconchegante. Conta com piscinas, quadras, salas de dança e restaurantes.<br><br>Levando o nome do bairro, o Clube Alto dos Pinheiros foi fundado em 1960 pelos moradores do bairro para criar uma extensão de seus lares. O clube oferece atividades esportivas, sociais e culturais, conta com três piscinas, ginásio poliesportivo, quadras de tênis, areia, campo de futebol society, academia, cinema, playground e até spa, além de restaurantes.'
			},
			images: [
				{
					url: '/images/regions/alto-de-pinheiros/content/content-3.webp',
					credits: 'Tumblr Kureator'
				},
				{
					url: '/images/regions/alto-de-pinheiros/content/content-4.webp',
					credits: 'Cristóbal Palma'
				}
			]
		},
		{
			info: {
				title: `Parques no ${regionName}`,
				content:
					'Na extremidade do Alto de Pinheiros estão localizados dois parques queridos dos moradores da Zona Oeste: Parque Villa-Lobos e Parque Cândido Portinari.<br><br>Integrados, os parques são perfeitos para um passeio nos finais de semana, seja com a sua bicicleta, seja alugando uma na porta, seja correndo, tomando uma água de coco ou apenas aproveitando um dia de sol em São Paulo. Eles ainda contam com um calendário de eventos ao longo do ano, que vai de peças de teatro à feiras gastronômicas, transformando o local a cada mês. Vale ficar de olho.'
			},
			images: [
				{ url: '/images/regions/alto-de-pinheiros/content/content-5.webp' }
			]
		},
		{
			info: {
				title: `Imóvel icônico no ${regionName}`,
				content:
					'Sonha em morar em um imóvel assinado? A Casa Oscar Niemeyer, no Alto de Pinheiros, está disponível para compra. Com 70 angulações diferentes, a construção leva a assinatura do arquiteto brasileiro em todas as suas curvas. Com cinco suítes e seis vagas de garagem, a planta do imóvel foi um presente ao amigo Milton José Mitidieri, que a construiu nos anos 70. O paisagismo assinado por Burle Marx adiciona mais um ponto de natureza no verde Alto de Pinheiros. ',
				class: 'lg:col-start-2'
			},
			images: [
				{
					url: '/images/regions/alto-de-pinheiros/content/content-6.webp',
					class: 'row-start-2',
					credits: 'MO1061'
				},
				{
					url: '/images/regions/alto-de-pinheiros/content/content-7.webp',
					class: 'row-start-2',
					credits: 'MO1061'
				}
			]
		}
	],
	seo: {
		title: `Quem se interessou por ${regionName} também procurou por`,
		links: [
			{
				title: 'Tipo de imóvel',
				links: [
					{
						label: 'Casa',
						route: PropertyFiltersPresenter.getPathFromFilters({
							propertyTypes: [PropertyTypeEnum.HOUSE],
							states: ['sp'],
							cities: ['São Paulo'],
							regions: [regionName]
						})
					},
					{
						label: 'Apartamento',
						route: PropertyFiltersPresenter.getPathFromFilters({
							propertyTypes: [PropertyTypeEnum.APARTMENT],
							states: ['sp'],
							cities: ['São Paulo'],
							regions: [regionName]
						})
					},
					{
						label: 'Casa em condomínio',
						route: PropertyFiltersPresenter.getPathFromFilters({
							propertyTypes: [PropertyTypeEnum.CONDO_HOUSE],
							states: ['sp'],
							cities: ['São Paulo'],
							regions: [regionName]
						})
					}
				]
			},
			{
				title: ' ',
				links: [
					{
						label: 'Cobertura',
						route: PropertyFiltersPresenter.getPathFromFilters({
							propertyTypes: [PropertyTypeEnum.PENTHOUSE],
							states: ['sp'],
							cities: ['São Paulo'],
							regions: [regionName]
						})
					},
					{
						label: 'Garden',
						route: PropertyFiltersPresenter.getPathFromFilters({
							propertyTypes: [PropertyTypeEnum.GARDEN],
							states: ['sp'],
							cities: ['São Paulo'],
							regions: [regionName]
						})
					},
					{
						label: 'Duplex',
						route: PropertyFiltersPresenter.getPathFromFilters({
							propertyTypes: [PropertyTypeEnum.DUPLEX],
							states: ['sp'],
							cities: ['São Paulo'],
							regions: [regionName]
						})
					}
				]
			},
			{
				title: 'Localização',
				links: [
					{
						label: 'Jardim Paulista, São Paulo',
						route: PropertyFiltersPresenter.getPathFromFilters({
							states: ['sp'],
							cities: ['São Paulo'],
							regions: ['Jardim Paulista']
						})
					},
					{
						label: 'Vila Nova Conceição, São Paulo',
						route: PropertyFiltersPresenter.getPathFromFilters({
							states: ['sp'],
							cities: ['São Paulo'],
							regions: ['Vila Nova Conceição']
						})
					},
					{
						label: 'Jardim América, São Paulo',
						route: PropertyFiltersPresenter.getPathFromFilters({
							states: ['sp'],
							cities: ['São Paulo'],
							regions: ['Jardim América']
						})
					}
				]
			},
			{
				title: ' ',
				links: [
					{
						label: 'Higienópolis, São Paulo',
						route: PropertyFiltersPresenter.getPathFromFilters({
							states: ['sp'],
							cities: ['São Paulo'],
							regions: ['Higienópolis']
						})
					},
					{
						label: 'Brooklin, São Paulo',
						route: PropertyFiltersPresenter.getPathFromFilters({
							states: ['sp'],
							cities: ['São Paulo'],
							regions: ['Brooklin']
						})
					},
					{
						label: 'Moema, São Paulo',
						route: PropertyFiltersPresenter.getPathFromFilters({
							states: ['sp'],
							cities: ['São Paulo'],
							regions: ['Moema']
						})
					}
				]
			}
		],
		discoveryContent: {
			heading: `Mais sobre o ${regionName}`,
			paragraphs: [
				'O Alto de Pinheiros é um refúgio na zona oeste de São Paulo, situado próximo aos vibrantes bairros de Pinheiros e Vila Madalena. Com ruas arborizadas e diversas praças, o bairro combina tranquilidade e vida urbana, ideal para quem busca um equilíbrio entre lazer e bem-estar. O Parque Villa-Lobos é um dos principais atrativos, enquanto o Shopping Villa-Lobos, com sua variedade de lojas e serviços, complementa a experiência de viver na região.',
				'O bairro abriga renomados restaurantes, que variam de tradicionais a contemporâneos, como o CÓR e Dona Deola. Para socializar, o bairro conta com clubes como o Anhembi Tênis Clube e o Clube Alto dos Pinheiros, que oferecem diversas atividades esportivas e sociais. Os parques, incluindo o Parque Villa-Lobos e o Parque Cândido Portinari, são perfeitos para atividades ao ar livre. Os imóveis incluem a icônica Casa Oscar Niemeyer, projetada por um dos arquitetos modernos mais renomados da arquitetura brasileira e com paisagismo assinado por Burle Marx, um dos mais importantes paisagistas do pais. O Alto de Pinheiros é o lugar ideal para quem valoriza um estilo de vida tranquilo, mas imerso em referências culturais, e aprecia o que há de mais exclusivo e criativo na arquitetura moderna paulistana.'
			]
		}
	},
	nearRegions: [
		{
			image: '/images/regions/itaim-bibi/banner.webp',
			name: 'Itaim Bibi',
			href: '/bairros/itaim-bibi'
		},
		{
			image: '/images/regions/jardim-paulista/banner.webp',
			name: 'Jardim Paulista',
			href: '/bairros/jardim-paulista'
		},
		{
			image: '/images/regions/vila-nova-conceicao/banner.webp',
			name: 'Vila Nova Conceição',
			href: '/bairros/vila-nova-conceicao'
		},
		{
			image: '/images/regions/jardim-america/banner.webp',
			name: 'Jardim América',
			href: '/bairros/jardim-america'
		},
		{
			image: '/images/regions/pinheiros/banner.webp',
			name: 'Pinheiros',
			href: '/bairros/pinheiros'
		},
		{
			image: '/images/regions/jardim-europa/banner.webp',
			name: 'Jardim Europa',
			href: '/bairros/jardim-europa'
		},
		{
			image: '/images/regions/jardim-paulistano/banner.webp',
			name: 'Jardim Paulistano',
			href: '/bairros/jardim-paulistano'
		}
	]
};

export default region;
